<template>
  <div v-if="!vaCandidatesLoading" class="mx-auto max-w-2xl">
    <div
      v-if="defaultPage"
      class="m-4 mx-auto my-6 flex flex-col rounded-lg bg-white p-4"
    >
      <div
        class="text-vgmedturq my-6 flex justify-center text-center text-xl font-bold"
      >
        We've got reliable and vetted remote talent in Canada and the US!
      </div>
      <div class="grid grid-cols-3 place-items-center">
        <div
          class="text-vgmedturq-800 flex flex-row items-center justify-center gap-1 text-xl font-bold md:text-3xl"
        >
          <span>386k</span>
          <span class="text-vgmedturq text-xs leading-3">
            <div>Hours</div>
            <div>Saved</div>
          </span>
        </div>
        <div
          class="text-vgmedturq-800 flex flex-row items-center justify-center gap-1 text-xl font-bold md:text-3xl"
        >
          800+
          <span class="text-vgmedturq text-xs leading-3">
            <div>Support</div>
            <div>Skills</div>
          </span>
        </div>
        <div
          class="text-vgmedturq-800 flex flex-row items-center justify-center gap-1 text-xl font-bold md:text-3xl"
        >
          700+
          <span class="text-vgmedturq text-xs leading-3">
            <div>Virtual</div>
            <div>Assistants</div>
          </span>
        </div>
      </div>
      <div
        v-if="!error"
        class="mx-auto mt-6 flex flex-col items-baseline lg:mt-12 lg:flex-row"
      >
        <div class="mb-6 pl-4">
          <span v-for="(candidate, id) in defaultCandidates" :key="id">
            <v-avatar class="-ml-3 border-2 border-white" size="48">
              <v-img
                class="inline object-cover"
                :src="getAvatar(candidate)"
                lazy-src="/img/profile.png"
                alt="Profile image"
              />
            </v-avatar>
          </span>
          <span>
            <v-avatar class="bg-vgorange text- -ml-2" size="48">
              <span class="text-xl font-bold text-white">+</span>
            </v-avatar>
          </span>
        </div>
        <span class="text-vgmedturq mx-6 font-bold">
          Whatever you need, we're your people!
        </span>
      </div>
      <div
        class="text-vgnavy my-6 grid grid-cols-1 text-xs font-bold lg:mx-auto lg:grid-cols-3"
      >
        <div
          v-for="(role, id) in roles"
          :key="id"
          class="hover:bg-vgnavy m-2 rounded-lg bg-white p-4 drop-shadow-md transition-colors duration-300 hover:text-white"
        >
          <div class="flex flex-row items-center gap-2">
            <v-img
              class="min-w-[1rem] max-w-[2rem]"
              :src="role.icon"
              :alt="`${role} icon`"
            />
            <span>{{ role.label }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div
        class="text-vgmedturq mt-6 flex flex-col text-center text-xl font-bold lg:flex-row lg:justify-center"
      >
        Great news! we've found
        <span class="text-vgorange mx-1">
          {{ vaCandidates?.length }} potential virtual assistant
        </span>
        matches for you!
      </div>
      <div class="text-small text-vgmedturq mt-4 text-center">
        Take the next step to connect with qualified and vetted talents
      </div>
      <div class="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
        <div
          v-for="(assistant, id) in vaCandidates.slice(0, 5)"
          :key="id"
          class="m-2 mx-auto flex w-[18rem] flex-col rounded-b-2xl bg-white p-4 shadow-sm hover:shadow-lg lg:m-4 lg:w-[15rem]"
        >
          <div>
            <div class="flex flex-row items-center">
              <v-avatar
                size="70"
                class="rounded-full border-4 border-white shadow-md"
              >
                <v-img
                  :src="getAvatar(assistant)"
                  lazy-src="/img/profile.png"
                  alt="Profile image"
                >
                </v-img>
              </v-avatar>
              <p class="m-2 truncate text-left text-sm font-bold">
                {{ assistant.display_name }}
              </p>
            </div>
            <div class="cut my-2 h-[8rem] overflow-hidden text-left text-xs">
              {{ assistant.blurb }}
            </div>
          </div>
          <div>
            <div class="my-2 flex flex-row items-baseline">
              <div class="text-vgmedturq text-left text-sm font-bold">
                Roles
              </div>
              <div class="border-vgteal-400 ml-1 grow border-b-[2px]"></div>
            </div>
            <div class="flex flex-wrap gap-1">
              <v-chip
                v-for="(role, idx) in getRoles(assistant)?.slice(0, 3)"
                :key="idx"
                class="bg-vgsoftblue"
                size="small"
              >
                <span
                  class="w-[6rem] overflow-hidden text-ellipsis font-semibold lg:w-full"
                  style="font-size: 10px"
                >
                  {{ role }}
                </span>
              </v-chip>
              <v-chip
                v-if="getRoles(assistant)?.length - 3 > 0"
                class="bg-vgsoftblue pl-2 pr-4 font-semibold"
                style="font-size: 10px"
                size="small"
              >
                +{{ getRoles(assistant)?.length - 3 }}
              </v-chip>
            </div>
          </div>
        </div>
        <span
          class="text-vgorange flex place-self-center p-4 text-3xl font-bold"
        >
          +{{ vaCandidates.length - 5 }}
          more
          <br />
          Talent..
        </span>
      </div>
    </div>
  </div>
</template>
<script>
import { nextTick } from 'vue';
import { getVAMatches } from '@/services/virtualAssistantsService';
import { mapStores } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import { API_URL } from '@/config';

export default {
  name: 'ClientCredibility',
  data() {
    return {
      error: false,
      roles: [
        {
          label: 'Administrative Assistant',
          icon: '/svg/administrative-icon.svg',
        },
        {
          label: 'Marketing Assistant',
          icon: '/svg/marketing-icon.svg',
        },
        {
          label: 'Social Media Assistant',
          icon: '/svg/social-media-icon.svg',
        },
        {
          label: 'Sales Assistant',
          icon: '/svg/sales-icon.svg',
        },
        {
          label: 'Bookkeeping Assistant',
          icon: '/svg/bookeeping-icon.svg',
        },
        {
          label: 'Real Estate Assistant',
          icon: '/svg/real-estate-icon.svg',
        },
        {
          label: 'Legal Assistant',
          icon: '/svg/legal-icon.svg',
        },
        {
          label: 'Medical Assistant',
          icon: '/svg/medical-icon.svg',
        },
        {
          label: 'Executive Assistant',
          icon: '/svg/executive-icon.svg',
        },
      ],
      vaCandidates: [],
      vaCandidatesLoading: false,
      defaultCandidates: [],
    };
  },
  computed: {
    ...mapStores(useCheckoutStore),
    defaultPage() {
      return this.vaCandidates?.length < 50;
    },
  },
  watch: {
    'checkoutStore.matchingCriteria': {
      deep: true,
      immediate: true,
      handler() {
        this.setup();
      },
    },
  },
  mounted() {
    this.setup();
  },
  methods: {
    getAvatar(assistant) {
      if (assistant.avatar_id === -1) {
        return '/img/profile.png';
      }

      return `${API_URL}/file/avatar/${assistant.avatar_id}`;
    },
    async setup() {
      this.error = false;

      try {
        await this.checkoutStore.getLocalRoles();
        await this.searchCandidates();
        nextTick(async () => {
          if (this.defaultPage) {
            await this.getAllCandidates();
          }
        });
      } catch (error) {
        this.vaCandidatesLoading = false;
        this.error = true;
      }
    },
    async getAllCandidates() {
      const rids = this.checkoutStore.localRoles.map((role) => {
        return role.id;
      });

      let filter = {};

      filter['rids'] = rids?.length > 0 ? Object.values(rids) : [];
      filter['pagination.limit'] = 6;
      this.defaultCandidates = await this.getVAs(filter);
    },
    getRoles(assistant) {
      return assistant.roles?.split(', ');
    },
    async searchCandidates() {
      const rids = this.checkoutStore.matchingCriteria?.keywords?.roles.map(
        (item) => {
          const foundItem = this.checkoutStore.localRoles.find(
            (role) => role.value === item
          )?.id;
          return foundItem;
        }
      );

      let filter = {};

      filter['pagination.limit'] = 40;
      filter['rids'] = rids?.length > 0 ? Object.values(rids) : [];

      if (this.checkoutStore.matchingCriteria?.tids?.length > 0) {
        filter['tids'] = this.checkoutStore.matchingCriteria.tids;
      }

      if (this.checkoutStore.matchingCriteria?.tids?.length > 0) {
        filter['sids'] = this.checkoutStore.matchingCriteria.sids;
      }

      this.vaCandidates = await this.getVAs(filter);
    },
    async getVAs(filter) {
      this.vaCandidatesLoading = true;

      const matchesResult = await getVAMatches(
        new URLSearchParams({
          matchAnyRole: true,
          ...filter,
        })
      ).catch(() => {
        return {
          assistants: [],
          count: 0,
        };
      });

      this.vaCandidatesLoading = false;

      return matchesResult?.assistants;
    },
  },
};
</script>
<style scoped>
.cut {
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 8;
  line-clamp: 8;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  display: -moz-box;
}
</style>
